import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Clock,
  DollarSign,
  Briefcase,
  Calendar,
  Users,
  Truck,
  Share2,
  ExternalLink,
  Mail,
  Phone,
  X,
  Loader2
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import LoadingSpinner from '../components/LoadingSpinner';

const ApplicationModal = ({ isOpen, onClose, job }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    coverLetter: '',
    website: '' // Honeypot field
  });
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Convert CV file to base64 if present
      let cvBase64 = '';
      let cvFileName = '';
      let cvContentType = '';
      if (cvFile) {
        cvBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]); // strip data:...;base64, prefix
          reader.onerror = reject;
          reader.readAsDataURL(cvFile);
        });
        cvFileName = cvFile.name;
        cvContentType = cvFile.type || 'application/octet-stream';
      }

      const response = await fetch('/.netlify/functions/apply-to-job', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          cvBase64,
          cvFileName,
          cvContentType,
          jobTitle: job.title,
          jobSlug: job.slug,
          logicmelonId: job.logicmelon_id
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(data.error || 'Failed to submit application');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCvChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 5MB limit
      if (file.size > 5 * 1024 * 1024) {
        setError('CV file must be less than 5MB');
        e.target.value = '';
        return;
      }
      setCvFile(file);
      setError('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-c9-blue">Apply for Position</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <p className="text-gray-600 mt-2">{job?.title}</p>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-c9-blue mb-2">Application Submitted!</h3>
              <p className="text-gray-600 mb-6">
                Thank you for your application. We'll review it and get back to you within 2-3 business days.
              </p>
              <button
                onClick={onClose}
                className="bg-c9-orange hover:bg-c9-orange-alt text-white px-6 py-2 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />

              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  required
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c9-orange focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c9-orange focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c9-orange focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="coverLetter" className="block text-sm font-medium text-gray-700 mb-2">
                  Cover Letter
                </label>
                <textarea
                  id="coverLetter"
                  name="coverLetter"
                  rows={4}
                  value={formData.coverLetter}
                  onChange={handleChange}
                  placeholder="Tell us why you're interested in this position..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c9-orange focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="cv" className="block text-sm font-medium text-gray-700 mb-2">
                  Upload CV/Resume *
                </label>
                <input
                  type="file"
                  id="cv"
                  name="cv"
                  required
                  accept=".pdf,.doc,.docx"
                  onChange={handleCvChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c9-orange focus:border-transparent file:mr-4 file:py-1 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-c9-orange file:text-white hover:file:bg-c9-orange-alt file:cursor-pointer"
                />
                <p className="text-xs text-gray-500 mt-1">PDF, DOC or DOCX (max 5MB)</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-c9-orange hover:bg-c9-orange-alt text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      Submitting...
                    </div>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const RelatedJobs = ({ jobs, currentJobId }) => {
  if (!jobs || jobs.length === 0) return null;

  const formatSalary = (min, max) => {
    if (!min && !max) return 'Competitive';
    if (min && max) return `£${min.toLocaleString()} - £${max.toLocaleString()}`;
    if (min) return `From £${min.toLocaleString()}`;
    if (max) return `Up to £${max.toLocaleString()}`;
    return 'Competitive';
  };

  return (
    <div className="mt-16">
      <h2 className="text-2xl font-bold text-c9-blue mb-8">Related Opportunities</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {jobs.slice(0, 4).map((job) => (
          <div key={job.id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-c9-blue mb-2 hover:text-c9-orange transition-colors">
              <Link to={`/jobs/${job.slug}`}>
                {job.title}
              </Link>
            </h3>
            <div className="flex items-center text-gray-600 mb-3">
              <MapPin className="h-4 w-4 mr-2 text-c9-orange" />
              <span className="text-sm">{job.location}</span>
            </div>
            <div className="flex items-center text-sm text-gray-600 mb-4">
              <DollarSign className="h-4 w-4 mr-1 text-c9-orange" />
              {formatSalary(job.salary_min, job.salary_max)}
            </div>
            <Link
              to={`/jobs/${job.slug}`}
              className="text-c9-orange hover:text-c9-orange-alt font-medium text-sm transition-colors"
            >
              View Details →
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function JobDetails() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  useEffect(() => {
    const fetchJobDetails = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/.netlify/functions/get-job-details?slug=${slug}`);
        const data = await response.json();

        if (response.ok) {
          setJob(data.job);
          setRelatedJobs(data.relatedJobs || []);
        } else {
          setError(data.error || 'Job not found');
        }
      } catch (error) {
        console.error('Error fetching job details:', error);
        setError('Failed to load job details');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchJobDetails();
    }
  }, [slug]);

  const formatSalary = (min, max) => {
    if (!min && !max) return 'Competitive';
    if (min && max) return `£${min.toLocaleString()} - £${max.toLocaleString()}`;
    if (min) return `From £${min.toLocaleString()}`;
    if (max) return `Up to £${max.toLocaleString()}`;
    return 'Competitive';
  };

  const formatJobType = (type) => {
    const types = {
      'full-time': 'Full Time',
      'part-time': 'Part Time',
      'contract': 'Contract',
      'temporary': 'Temporary',
      'permanent': 'Permanent'
    };
    return types[type] || type;
  };

  const shareJob = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: job.title,
          text: `Check out this job opportunity: ${job.title} at C9 Recruitment`,
          url: window.location.href,
        });
      } catch (error) {
        // Fallback to clipboard
        navigator.clipboard.writeText(window.location.href);
      }
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-c9-blue mb-4">Job Not Found</h1>
          <p className="text-gray-600 mb-8">{error || 'The job you\'re looking for doesn\'t exist.'}</p>
          <Link
            to="/jobs"
            className="bg-c9-orange hover:bg-c9-orange-alt text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Browse All Jobs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={`${job.title} - C9 Recruitment`}
        description={job.description?.substring(0, 160) || `Apply for ${job.title} position at C9 Recruitment. Competitive salary and excellent benefits.`}
        keywords={`${job.title}, ${job.location}, ${job.category}, transport jobs, logistics careers`}
      />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container-full mx-auto px-4 py-4">
          <Link
            to="/jobs"
            className="flex items-center text-c9-orange hover:text-c9-orange-alt transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Job Listings
          </Link>
        </div>
      </div>

      <div className="container-full mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Job Header */}
              <div className="bg-gradient-to-r from-c9-blue to-c9-blue-alt text-white p-8">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <h1 className="text-3xl md:text-4xl font-bold mb-4">{job.title}</h1>
                    <div className="flex flex-wrap gap-4 text-lg">
                      <div className="flex items-center">
                        <MapPin className="h-5 w-5 mr-2 text-c9-orange" />
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center">
                        <Briefcase className="h-5 w-5 mr-2 text-c9-orange" />
                        <span>{formatJobType(job.job_type)}</span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-5 w-5 mr-2 text-c9-orange" />
                        <span>Posted {new Date(job.created_at).toLocaleDateString('en-GB')}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={shareJob}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Share2 className="h-6 w-6" />
                  </button>
                </div>

                <div className="flex flex-wrap gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                    <div className="flex items-center text-sm">
                      <DollarSign className="h-4 w-4 mr-2 text-c9-orange" />
                      <span>{formatSalary(job.salary_min, job.salary_max)}</span>
                    </div>
                  </div>
                  {job.category && (
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                      <div className="flex items-center text-sm">
                        <Truck className="h-4 w-4 mr-2 text-c9-orange" />
                        <span>{job.category}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Job Content */}
              <div className="p-8">
                <div className="prose max-w-none">
                  <div className="space-y-6">
                    {job.description && (
                      <div>
                        <h2 className="text-2xl font-bold text-c9-blue mb-4">Job Description</h2>
                        <div 
                          className="text-gray-700 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: job.description.replace(/\n/g, '<br>') }}
                        />
                      </div>
                    )}

                    {job.requirements && (
                      <div>
                        <h2 className="text-2xl font-bold text-c9-blue mb-4">Requirements</h2>
                        <div 
                          className="text-gray-700 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: job.requirements.replace(/\n/g, '<br>') }}
                        />
                      </div>
                    )}

                    {job.benefits && (
                      <div>
                        <h2 className="text-2xl font-bold text-c9-blue mb-4">Benefits</h2>
                        <div 
                          className="text-gray-700 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: job.benefits.replace(/\n/g, '<br>') }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Related Jobs */}
            <RelatedJobs jobs={relatedJobs} currentJobId={job.id} />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Apply Section */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-c9-blue mb-4">Apply for this Position</h3>
                <p className="text-gray-600 mb-6">
                  Ready to take the next step in your career? Apply now and our team will be in touch within 24 hours.
                </p>
                
                <div className="space-y-4">
                  <button
                    onClick={() => setShowApplicationModal(true)}
                    className="w-full bg-c9-orange hover:bg-c9-orange-alt text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    Apply Now
                  </button>
                  
                  {job.apply_url && (
                    <a
                      href={job.apply_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full border-2 border-c9-orange text-c9-orange hover:bg-c9-orange hover:text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Apply on External Site
                    </a>
                  )}
                </div>
              </div>

              {/* Contact Section */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-c9-blue mb-4">Need More Information?</h3>
                <p className="text-gray-600 mb-6">
                  Our recruitment specialists are here to help answer any questions about this role.
                </p>
                
                <div className="space-y-4">
                  <a
                    href="tel:02081524574"
                    className="flex items-center text-c9-orange hover:text-c9-orange-alt transition-colors"
                  >
                    <Phone className="h-5 w-5 mr-3" />
                    <span className="font-semibold">0208 152 4574</span>
                  </a>
                  
                  <a
                    href="mailto:recruitment@c9-recruitment.com"
                    className="flex items-center text-c9-orange hover:text-c9-orange-alt transition-colors"
                  >
                    <Mail className="h-5 w-5 mr-3" />
                    <span>recruitment@c9-recruitment.com</span>
                  </a>
                </div>
              </div>

              {/* Job Stats */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-c9-blue mb-4">Job Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Job Type:</span>
                    <span className="font-semibold">{formatJobType(job.job_type)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Category:</span>
                    <span className="font-semibold">{job.category || 'Transport'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location:</span>
                    <span className="font-semibold">{job.location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Salary:</span>
                    <span className="font-semibold">{formatSalary(job.salary_min, job.salary_max)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Posted:</span>
                    <span className="font-semibold">{new Date(job.created_at).toLocaleDateString('en-GB')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Application Modal */}
      <ApplicationModal 
        isOpen={showApplicationModal}
        onClose={() => setShowApplicationModal(false)}
        job={job}
      />
    </div>
  );
}
